import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { Text, Icon } from "@ui-component-library";
import VBLeaflet from "@vb-js-commons/new/vbLeaflet";
import Navigation from "../components/static/Navigation";
import Footer from "../components/static/Footer";
import QuoteSlider from "../components/commons/QuoteSlider";

const MAP_HEIGHT_PX = 672;

function getSkrytMapDataUrl() {
  const path = window.location.pathname.replace(/\/?$/, "");
  return `${path}/api/map-data/`;
}

/** Keep clusters merged until high zoom; disable animations for smoother pan/zoom. */
const CLUSTER_OPTIONS = {
  chunkedLoading: true,
  chunkInterval: 200,
  chunkDelay: 100,
  maxClusterRadius: 90,
  disableClusteringAtZoom: 17,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  animate: false,
  animateAddingMarkers: false,
  removeOutsideVisibleBounds: true,
};

/** Mirrors `.container` in tailwind.css so the light-DOM map aligns with page content. */
const SKRYT_MAP_SHELL_CSS = `
  .skryt-map-shell {
    width: 100%;
    padding-left: 1rem;
    padding-right: 1rem;
    margin-left: auto;
    margin-right: auto;
    margin-top: 1.5rem;
    padding-bottom: 2.5rem;
    box-sizing: border-box;
  }
  @media (min-width: 576px) {
    .skryt-map-shell { max-width: 540px; }
  }
  @media (min-width: 768px) {
    .skryt-map-shell { max-width: 720px; }
  }
  @media (min-width: 992px) {
    .skryt-map-shell { max-width: 960px; }
  }
  @media (min-width: 1200px) {
    .skryt-map-shell { max-width: 1140px; }
  }
  @media (min-width: 1400px) {
    .skryt-map-shell { max-width: 1320px; }
  }
  .skryt-map-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(15, 15, 15, 0.12);
    border-top-color: #2e7d32;
    border-radius: 50%;
    animation: skryt-map-spin 0.75s linear infinite;
  }
  @keyframes skryt-map-spin {
    to { transform: rotate(360deg); }
  }
  .skryt-map-marker-legend {
    pointer-events: none;
    position: absolute;
    right: 12px;
    bottom: 44px;
    z-index: 500;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 10px;
    background-color: rgba(255, 255, 255, 0.92);
    border: 1px solid #e7e5e4;
    box-shadow: 0 1px 3px rgba(15, 15, 15, 0.12);
  }
  @media (max-width: 767px) {
    .skryt-map-marker-legend {
      bottom: 56px;
    }
  }
`;

const formatStatNumber = (value, maximumFractionDigits = 0) => {
  if (value == null) {
    return "…";
  }
  return Number(value).toLocaleString("sv-SE", { maximumFractionDigits });
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatPopupDate = (value) => {
  if (!value) {
    return "–";
  }
  return new Date(value).toLocaleDateString("sv-SE");
};

const formatPopupVolume = (value) => {
  if (value == null) {
    return "–";
  }
  return `${formatStatNumber(value)} m³fub`;
};

function buildPropertyPopup(prop) {
  const designation = escapeHtml(prop.designation || `Fastighet ${prop.id}`);
  const area =
    prop.area != null ? `${formatStatNumber(prop.area)} ha` : "–";
  const created = formatPopupDate(prop.created_at);

  return `<div style="line-height:1.5">
    <strong>${designation}</strong><br />
    Areal: ${area}<br />
    Skapad: ${created}
  </div>`;
}

function buildAdPopup(ad) {
  const designation = escapeHtml(ad.designation || `Annons ${ad.id}`);
  const volume = formatPopupVolume(ad.volume);
  const created = formatPopupDate(ad.created_at);

  return `<div style="line-height:1.5">
    <strong>${designation}</strong><br />
    Volym: ${volume}<br />
    Skapad: ${created}
  </div>`;
}

const STAT_CONFIG = [
  {
    key: "total_sbp_area",
    label: "Hektar på plattformen",
    icon: "TreeSimple",
    format: (v) => formatStatNumber(v),
  },
  {
    key: "total_ad_volume",
    label: "Virkesvolym i marknaden",
    icon: "ScalesSimple",
    format: (v) => formatStatNumber(v),
  },
  {
    key: "registered_users",
    label: "Registrerade",
    icon: "UserCircleSimple",
    format: (v) => formatStatNumber(v),
  },
];

/** Reference figures for perspective copy (Swedish forestry context). */
const SWEDEN_PRIVATE_FOREST_HA = 11_300_000; // Skogsstyrelsen 2024: enskilda ägares produktiva skogsmark
const SWEDEN_ANNUAL_FELLING_M3FUB = 90_000_000;
const SWEDEN_PRIVATE_FOREST_OWNERS = 327_000;
const FOOTBALL_PITCH_HA = 0.714;
const TIMBER_TRUCK_M3FUB = 45;
const DETACHED_HOUSE_M3FUB = 15;

const formatPerspectivePercent = (value) =>
  Number(value).toLocaleString("sv-SE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

function StatHighlight({ children }) {
  return (
    <span className="whitespace-nowrap rounded-md bg-[#E8F5E9] px-1.5 py-0.5 font-medium text-[#2E7D32]">
      {children}
    </span>
  );
}

function StatPerspectives({ stats }) {
  const hasStats =
    stats?.total_sbp_area != null &&
    stats?.total_ad_volume != null &&
    stats?.registered_users != null;

  if (!hasStats) {
    return null;
  }

  const hectares = stats.total_sbp_area;
  const volume = stats.total_ad_volume;
  const users = stats.registered_users;

  const privateForestShare = formatPerspectivePercent(
    (hectares / SWEDEN_PRIVATE_FOREST_HA) * 100,
  );
  const footballPitches = formatStatNumber(Math.round(hectares / FOOTBALL_PITCH_HA));
  const harvestShare = formatPerspectivePercent((volume / SWEDEN_ANNUAL_FELLING_M3FUB) * 100);
  const timberTrucks = formatStatNumber(Math.round(volume / TIMBER_TRUCK_M3FUB));
  const houses = formatStatNumber(Math.round(volume / DETACHED_HOUSE_M3FUB));
  const ownerShare = formatPerspectivePercent((users / SWEDEN_PRIVATE_FOREST_OWNERS) * 100);

  return (
    <div className="mx-auto mt-12 w-full max-w-[768px] space-y-5 text-center lg:mt-16">
      <Text variant="text-xl/leading-normal/medium" className="text-charcoal-500">
        I perspektiv
      </Text>
      <Text variant="text-base/leading-normal/normal" className="text-pebble-600">
        Treebulas{" "}
        <StatHighlight>{formatStatNumber(hectares)} hektar</StatHighlight> motsvarar
        ungefär <StatHighlight>{privateForestShare} %</StatHighlight> av all
        privatägd produktiv skogsmark i Sverige - eller lika mycket mark som{" "}
        <StatHighlight>{footballPitches} fotbollsplaner.</StatHighlight>
      </Text>
      <Text variant="text-base/leading-normal/normal" className="text-pebble-600">
        De <StatHighlight>{formatStatNumber(volume)} m³fub</StatHighlight> som finns i
        plattformens annonser motsvarar ungefär{" "}
        <StatHighlight>{harvestShare} %</StatHighlight> av Sveriges årliga
        avverkningsvolym - eller virke nog att lasta{" "}
        <StatHighlight>{timberTrucks} timmerbilar</StatHighlight> och bygga cirka{" "}
        <StatHighlight>{houses} villor.</StatHighlight>
      </Text>
      <Text variant="text-base/leading-normal/normal" className="text-pebble-600">
        <StatHighlight>{formatStatNumber(users)} registrerade skogsägare</StatHighlight>{" "}
        motsvarar ungefär <StatHighlight>{ownerShare} %</StatHighlight> av alla
        enskilda skogsägare i Sverige.
      </Text>
    </div>
  );
}

function createMarkerIcon(path) {
  return L.icon({
    iconUrl: `${Window.static_path}${path}`,
    iconSize: [32, 32],
    shadowSize: [32, 32],
    iconAnchor: [16, 32],
    shadowAnchor: [16, 32],
    popupAnchor: [0, -16],
  });
}

function createClusterGroup(clusterClass, onChunksLoaded) {
  return L.markerClusterGroup({
    ...CLUSTER_OPTIONS,
    chunkProgress: (processed, total) => {
      if (processed >= total && onChunksLoaded) {
        onChunksLoaded();
      }
    },
    iconCreateFunction: (cluster) => {
      const count = cluster.getChildCount();
      let size = "small";
      if (count > 100) {
        size = "large";
      } else if (count > 20) {
        size = "medium";
      }
      return L.divIcon({
        html: `<div><span>${count}</span></div>`,
        className: `marker-cluster marker-cluster-${clusterClass} marker-cluster-${size}`,
        iconSize: L.point(40, 40),
      });
    },
  });
}

function clearSkrytMapLayers(map, markersLayerRef) {
  if (!markersLayerRef.current) {
    return;
  }
  markersLayerRef.current.forEach((layer) => map.removeLayer(layer));
  markersLayerRef.current = null;
}

function populateSkrytMap(mapRef, markersLayerRef, data) {
  const map = mapRef.current?.state?.map;
  if (!map || !data || typeof L.markerClusterGroup !== "function") {
    return Promise.resolve({ propertyCount: 0, adCount: 0 });
  }

  clearSkrytMapLayers(map, markersLayerRef);

  const bounds = L.latLngBounds();
  const propertyIcon = createMarkerIcon("img/map-markers/marker-tree.svg");
  const adIcon = createMarkerIcon("img/map-markers/marker_pin_secondary-orange.svg");

  const propertyMarkers = data.properties.map((prop) => {
    const { latitude, longitude } = prop;
    const marker = L.marker([latitude, longitude], { icon: propertyIcon });
    marker.bindPopup(buildPropertyPopup(prop));
    bounds.extend([latitude, longitude]);
    return marker;
  });

  const adMarkers = data.ads.map((ad) => {
    const { latitude, longitude } = ad;
    const marker = L.marker([latitude, longitude], { icon: adIcon });
    marker.bindPopup(buildAdPopup(ad));
    bounds.extend([latitude, longitude]);
    return marker;
  });

  if (propertyMarkers.length === 0 && adMarkers.length === 0) {
    return Promise.resolve({ propertyCount: 0, adCount: 0 });
  }

  return new Promise((resolve) => {
    let clustersPending = 0;
    let settled = false;
    let safetyTimeout;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(safetyTimeout);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
      resolve({
        propertyCount: data.properties.length,
        adCount: data.ads.length,
      });
    };

    safetyTimeout = setTimeout(finish, 15000);

    const onClusterReady = () => {
      clustersPending -= 1;
      if (clustersPending <= 0) {
        finish();
      }
    };

    const layers = [];

    if (propertyMarkers.length > 0) {
      clustersPending += 1;
      const propertyCluster = createClusterGroup("property", onClusterReady);
      propertyCluster.addLayers(propertyMarkers);
      propertyCluster.addTo(map);
      layers.push(propertyCluster);
    }

    if (adMarkers.length > 0) {
      clustersPending += 1;
      const adCluster = createClusterGroup("ad", onClusterReady);
      adCluster.addLayers(adMarkers);
      adCluster.addTo(map);
      layers.push(adCluster);
    }

    markersLayerRef.current = layers;

    if (clustersPending === 0) {
      finish();
    }
  });
}

const MAP_MARKER_LEGEND = [
  {
    icon: "img/map-markers/marker-tree.svg",
    label: "Skogsbruksplan",
  },
  {
    icon: "img/map-markers/marker_pin_secondary-orange.svg",
    label: "Virkesannons",
  },
];

function SkrytMapMarkerLegend() {
  return (
    <div className="skryt-map-marker-legend" aria-label="Kartförklaring">
      {MAP_MARKER_LEGEND.map((item) => (
        <div
          key={item.label}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <img
            src={`${Window.static_path}${item.icon}`}
            alt=""
            width={20}
            height={20}
            style={{ display: "block", flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "16px",
              color: "#0f0f0f",
            }}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function SkrytMap({ mapRef, mapLegend, mapLoading }) {
  return (
    <div
      style={{
        overflow: "hidden",
        borderRadius: "16px",
        border: "1px solid #e7e5e4",
        backgroundColor: "#fafaf9",
        boxShadow: "0 1px 2px rgba(15, 15, 15, 0.06)",
      }}
    >
      <div style={{ position: "relative", height: `${MAP_HEIGHT_PX}px`, width: "100%" }}>
        <VBLeaflet
          ref={mapRef}
          id="skryt_map"
          baseMap="Mapbox Satelite"
          style={{ position: "absolute", inset: 0, height: "100%", width: "100%" }}
        />
        {mapLoading ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.82)",
            }}
            aria-busy="true"
            aria-live="polite"
          >
            <div className="skryt-map-spinner" role="status" aria-label="Laddar kartdata" />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                color: "#0f0f0f",
              }}
            >
              Laddar kartdata…
            </p>
          </div>
        ) : null}
        <div
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "1rem 1.5rem",
            background: "linear-gradient(to top, rgba(15, 15, 15, 0.4), transparent)",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              color: "#ffffff",
            }}
          >
            {mapLegend || "Laddar karta…"}
          </p>
        </div>
        <SkrytMapMarkerLegend />
      </div>
    </div>
  );
}

export default function SkrytPage() {
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [mapHost, setMapHost] = useState(null);
  const [mapLegend, setMapLegend] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const mapHostRef = useCallback((node) => {
    setMapHost(node);
  }, []);

  const loadMapData = useCallback(async () => {
    const map = mapRef.current?.state?.map;
    if (!map) {
      return;
    }

    setMapLoading(true);

    try {
      const response = await fetch(getSkrytMapDataUrl(), { credentials: "same-origin" });
      if (!response.ok) {
        setMapLegend("Kunde inte ladda kartdata");
        return;
      }

      const data = await response.json();
      const counts = await populateSkrytMap(mapRef, markersLayerRef, data);
      setStats({
        total_sbp_area: data.stats?.total_sbp_area,
        total_ad_volume: data.stats?.total_ad_volume,
        registered_users: data.stats?.registered_users,
      });
      setMapLegend(
        `${counts.propertyCount} skogsbruksplaner · ${counts.adCount} aktiva virkesannonser`,
      );
    } catch {
      setMapLegend("Kunde inte ladda kartdata");
    } finally {
      setMapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!mapHost) {
      return undefined;
    }

    let cancelled = false;

    const tryInitMap = (attempt = 0) => {
      if (cancelled) {
        return;
      }

      const map = mapRef.current?.state?.map;
      if (map) {
        map.invalidateSize();
        loadMapData();
        return;
      }

      if (attempt < 25) {
        setTimeout(() => tryInitMap(attempt + 1), 100);
      }
    };

    tryInitMap();

    return () => {
      cancelled = true;
      const map = mapRef.current?.state?.map;
      if (map) {
        clearSkrytMapLayers(map, markersLayerRef);
      }
    };
  }, [mapHost, loadMapData]);

  return (
    <div style={{ width: "100%" }}>
      <Navigation />

      <TailwindWrapper>
        <section className="w-full bg-[#F6F5F4]">
          <div className="container py-12 lg:py-20">
            <div className="mx-auto w-full max-w-[768px] text-center">
              <Text
                variant="text-sm/leading-normal/medium"
                className="mb-3 uppercase tracking-[0.12em] text-pebble-500"
              >
                Treebula i siffror
              </Text>
              <Text
                as="h1"
                variant="text-4xl/leading-normal/medium"
                className="mb-4 text-charcoal-500 lg:text-[48px] lg:leading-[56px]"
              >
                Sveriges skog, digitaliserad i realtid
              </Text>
              <Text
                variant="text-base/leading-normal/normal"
                className="text-pebble-600"
              >
                Treebula samlar planering, marknadsplats och datadriven
                skogsförvaltning på en plattform. Utforska vår geografiska
                täckning, följ nyckeltal som visar tillväxt och aktivitet,
                och läs vad skogsägare säger om sin upplevelse på Treebula.
              </Text>
            </div>
          </div>
        </section>

        <section className="container w-full pb-2 pt-10 lg:pb-4 lg:pt-16">
          <div className="flex w-full flex-col gap-2">
            <Text variant="text-2xl/leading-normal/medium" className="text-charcoal-500">
              Plattformens räckvidd
            </Text>
            <Text
              variant="text-base/leading-normal/normal"
              className="w-full max-w-[672px] text-pebble-600"
            >
              Kartan visar var skogsägare använder Treebula idag. Gröna markörer
              är skogsbruksplaner, orangea markörer är virkesannonser.
              Du kan zooma in för att se enskilda fastigheter och annonser.
            </Text>
          </div>
        </section>
      </TailwindWrapper>

      {/*
        VBLeaflet must stay in the light DOM — Leaflet uses document.getElementById
        for the container id, which cannot resolve nodes inside TailwindWrapper's shadow root.
      */}
      <div className="skryt-map-shell">
        <div ref={mapHostRef} className="w-full" />
      </div>
      {mapHost &&
        ReactDOM.createPortal(
          <SkrytMap mapRef={mapRef} mapLegend={mapLegend} mapLoading={mapLoading} />,
          mapHost,
        )}

      <style>{SKRYT_MAP_SHELL_CSS}</style>

      <TailwindWrapper>
        <section className="w-full bg-[#F6F5F4]">
          <div className="container py-12 lg:py-20">
            <div className="mb-10 w-full text-center lg:mb-14">
              <Text variant="text-2xl/leading-normal/medium" className="mb-2 text-charcoal-500">
                Nyckeltal
              </Text>
              <Text
                variant="text-base/leading-normal/normal"
                className="mx-auto w-full max-w-[640px] text-pebble-600"
              >
                Nyckeltal som speglar plattformens tillväxt och engagemang
                bland svenska skogsägare
              </Text>
            </div>

            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-3 lg:gap-8">
              {STAT_CONFIG.map((stat) => (
                <div
                  key={stat.key}
                  className="flex flex-col items-center rounded-2xl border border-stone-200 bg-white px-6 py-8 text-center shadow-sm"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#E8F5E9]">
                    <Icon name={stat.icon} size="medium" className="text-[#2E7D32]" />
                  </div>
                  <Text variant="text-4xl/leading-normal/medium" className="mb-2 text-charcoal-500">
                    {stat.format(stats?.[stat.key])}
                  </Text>
                  <Text variant="text-base/leading-normal/normal" className="text-pebble-600">
                    {stat.label}
                  </Text>
                </div>
              ))}
            </div>

            <StatPerspectives stats={stats} />
          </div>
        </section>

        <QuoteSlider backgroundColor="#FFFFFF" template="template1" />
      </TailwindWrapper>

      <Footer />
    </div>
  );
}
